import { useState, useEffect } from "react";
import { z } from "zod";
import { useSetAtom } from "jotai";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { forgotPasswordDialog, loginDialog } from "@/state/dialogs";
import { LoadingButton } from "@/components/ui/loading-button";
import { PATHS } from "@/config/paths";
import { useRouter, Link } from "@tanstack/react-router";
import { useMobile } from "@/providers/mobileProvider";
import { signIn } from "@/lib/auth-client";
import { useUser } from "@/hooks/useUser";
import { useTranslation } from "@/hooks/useTranslation";

const createLoginFormSchema = (t: (key: string) => string) =>
  z.object({
    email: z.string().email(t("forms.invalid_email")),
    password: z.string().min(1, t("forms.password_required")),
  });

export type LoginValues = z.infer<ReturnType<typeof createLoginFormSchema>>;

export const LoginForm = () => {
  const { refetchUser } = useUser();
  const router = useRouter();
  const { isMobile } = useMobile();
  const setLoginDialogOpen = useSetAtom(loginDialog);
  const setForgotPaswordDialogOpen = useSetAtom(forgotPasswordDialog);
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (shouldRedirect) {
      router.navigate({ to: PATHS.DASHBOARD, replace: true });
    }
  }, [shouldRedirect, router]);

  const loginFormSchema = createLoginFormSchema((key: string) => t(key) as string);

  const form = useForm({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const handleSubmit = async (values: LoginValues) => {
    setLoadingLogin(true);

    try {
      const { error } = await signIn.email({
        email: values.email,
        password: values.password,
        rememberMe: true,
      });

      if (error) {
        if (error.code === "INVALID_EMAIL_OR_PASSWORD") {
          form.setError("email", { message: t("forms.invalid_email_or_password") });
        } else {
          form.setError("email", {
            message: t("forms.login_error"),
          });
        }
        return;
      }

      refetchUser();

      setLoginDialogOpen(false);
      setShouldRedirect(true);
    } catch (error) {
      form.setError("email", {
        message: t("forms.login_error_generic"),
      });
    } finally {
      setLoadingLogin(false);
    }
  };

  const goToForgotPassword = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (isMobile) {
      router.navigate({ to: PATHS.FORGOT_PASSWORD });
    } else {
      setLoginDialogOpen(false);
      setForgotPaswordDialogOpen(true);
    }
  };

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="mb-4">
                <FormLabel>{t("forms.email")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("forms.email_placeholder")}
                    autoComplete="email"
                    autoFocus
                    required
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="mb-4">
                <FormLabel>{t("forms.password")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("forms.password_placeholder")}
                    type="password"
                    required
                    autoComplete="current-password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <LoadingButton
            data-umami-event="login-form-submit"
            type="submit"
            loading={loadingLogin}
            className="w-full"
          >
            {t("forms.login")}
          </LoadingButton>
        </form>
      </Form>

      <p className="mt-2 text-balance text-center text-sm leading-loose text-muted-foreground">
        <Link
          to={PATHS.FORGOT_PASSWORD}
          onClick={goToForgotPassword}
          className="underline hover:text-primary"
        >
          {t("forms.forgot_password_link")}
        </Link>
      </p>
    </>
  );
};
